import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { Star, Calendar, Handshake } from "lucide-react";
import ReviewModal from "../components/ReviewModal";
import StarRating from "../components/StarRating";
import { format, parseISO } from "date-fns";

export default function ReviewsInbox() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [pending, setPending] = useState([]);
  const [received, setReceived] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    refresh();
  }, [user, authLoading, nav]);

  const refresh = async () => {
    try {
      const [pendingRes, receivedRes] = await Promise.all([
        api.get("/reviews/reviewable"),
        api.get(`/reviews?user_id=${user.id}`),
      ]);
      setPending(pendingRes.data);
      setReceived(receivedRes.data);
    } catch (err) { console.error("[reviews-inbox] load failed", err); }
  };

  if (authLoading || !user) return null;

  return (
    <div data-testid="reviews-inbox-page" className="pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122] flex items-center gap-2">
          <Star size={12} strokeWidth={1.5} /> {t("reviews")}
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {t("reviews_inbox")}
        </h1>
        <p className="mt-3 text-sm text-[#1A1F1C]/70 max-w-2xl">
          {t("reviews_inbox_sub")}
        </p>

        {/* Pending */}
        <div className="mt-10">
          <h2 className="font-serif-display text-2xl text-[#143122]">
            {t("pending_reviews")} <span className="text-[#1A1F1C]/40">({pending.length})</span>
          </h2>
          <div className="mt-4 grid gap-3">
            {pending.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-12 text-center">
                {t("no_pending_reviews")}
              </div>
            ) : pending.map((p) => (
              <div key={`${p.context_type}-${p.context_id}`} data-testid={`pending-${p.context_id}`} className="border border-[#E6E2D8] rounded-lg p-4 bg-white flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-[#143122]">
                    {p.context_type === "booking" ? <Calendar size={18} strokeWidth={1.5} /> : <Handshake size={18} strokeWidth={1.5} />}
                  </div>
                  <div>
                    <div className="overline text-[#143122]">{p.context_type}</div>
                    <div className="font-serif-display text-lg text-[#1A1F1C]">{p.title}</div>
                    <div className="text-xs text-[#1A1F1C]/60 mt-0.5">
                      {t("with")} {p.subject_name} · {format(parseISO(p.when), "d MMM yyyy")}
                    </div>
                  </div>
                </div>
                <button
                  data-testid={`open-review-${p.context_id}`}
                  onClick={() => setActive(p)}
                  className="kav-btn-primary text-sm"
                >
                  {t("leave_review")}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Received */}
        <div className="mt-14">
          <h2 className="font-serif-display text-2xl text-[#143122]">{t("reviews_received")}</h2>
          <div className="mt-4 grid gap-3">
            {received.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-12 text-center">
                {t("no_reviews_received")}
              </div>
            ) : received.map((r) => (
              <div key={r.id} className="border border-[#E6E2D8] rounded-lg p-4 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-serif-display text-lg text-[#143122]">{r.author_name}</div>
                  <StarRating value={r.rating} readOnly size={16} />
                </div>
                {r.comment && <div className="mt-2 text-sm text-[#1A1F1C]/80">{r.comment}</div>}
                <div className="text-xs text-[#1A1F1C]/50 mt-2">{format(parseISO(r.created_at), "d MMM yyyy")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ReviewModal open={!!active} context={active} onClose={() => setActive(null)} onDone={refresh} />
    </div>
  );
}
